/**
 * Lens API types — request/response schemas, session state, and Zod validators.
 *
 * Consolidated from lens.ts + lens-v6.ts + lens-free-audit-v6.ts.
 * This file covers everything between the frontend and the backend API layer.
 * For the report contract (LensReport, LensFinding, etc.), see @/types/lens-ui.
 */

import { z } from 'zod'
import type { LensReport } from '@/types/lens-ui'

/** Minimum metadata boundary needed to render a canonical report safely.
 * The remaining report contract is owned by @lina/lens-contract.
 * Never invent an assessment type for incompatible or incomplete payloads.
 */
const LensReportMetadataBoundarySchema = z.object({
  metadata: z.object({ assessmentType: z.enum(['single', 'website_scan']) }),
})
const LensReportPayloadSchema = z.custom<LensReport>(
  (value): value is LensReport => LensReportMetadataBoundarySchema.safeParse(value).success,
  { message: 'Invalid Lens report: required assessment metadata is missing or invalid' },
)

// ===========================================================================
// Canonical types from @lina/lens-contract
// ===========================================================================

import { VOICE_DIMENSION_KEYS, type VoiceDimensionKey } from '@lina/lens-contract'

export { VOICE_DIMENSION_KEYS, type VoiceDimensionKey }
export type { DimensionScore } from '@lina/lens-contract/score-state'

const VoiceDimensionKeyEnum = z.enum(
  VOICE_DIMENSION_KEYS as unknown as [VoiceDimensionKey, ...VoiceDimensionKey[]],
)

// Zod schemas — re-created here because the contract exports TS types only.
// TODO: Move Zod schemas into @lina/lens-contract so all consumers share one source.

export const DimensionScoreSchema = z.union([
  z
    .number()
    .min(0)
    .max(100)
    .transform((value) => ({
      kind: 'number' as const,
      value,
    })),
  z.object({
    kind: z.literal('number'),
    value: z.number().min(0).max(100),
  }),
  z.object({ kind: z.literal('not_applicable') }),
  z.object({ kind: z.literal('not_assessable') }),
])

export const VoiceScoresSchema = z.object({
  visibility: DimensionScoreSchema,
  orientation: DimensionScoreSchema,
  inclusion: DimensionScoreSchema,
  clarity: DimensionScoreSchema,
  execution: DimensionScoreSchema,
})

export const DeterministicFindingSchema = z.object({
  ruleId: z.string(),
  title: z.string(),
  description: z.string(),
  severity: z.enum(['info', 'warning', 'critical']),
  voiceBadges: z.array(VoiceDimensionKeyEnum).min(1),
  evidence: z.string().optional(),
})

export const DeterministicStrengthSchema = z.object({
  ruleId: z.string(),
  title: z.string(),
  description: z.string(),
  voiceBadges: z.array(VoiceDimensionKeyEnum).min(1),
})

export const LlmFindingSchema = z.object({
  category: z.string(),
  title: z.string(),
  description: z.string(),
  voiceBadges: z.array(VoiceDimensionKeyEnum).min(1),
})

export const RecommendationSchema = z.object({
  title: z.string(),
  description: z.string(),
  priority: z.enum(['high', 'medium', 'low']),
  scope: z.enum(['page', 'site', 'document']),
  voiceBadges: z.array(VoiceDimensionKeyEnum).min(1),
  beforeExample: z.string().optional(),
  afterExample: z.string().optional(),
})

export const VoiceNarrativeSummarySchema = z.object({
  plainLanguageAssessment: z.string(),
})

export const DimensionNarrativeSchema = z.object({
  whatWorks: z.array(z.string()).default([]),
  frictionPoints: z.array(z.string()).default([]),
  recommendations: z.array(z.string()).default([]),
})

export const DimensionNarrativesSchema = z.object({
  visibility: DimensionNarrativeSchema.optional(),
  orientation: DimensionNarrativeSchema.optional(),
  inclusion: DimensionNarrativeSchema.optional(),
  clarity: DimensionNarrativeSchema.optional(),
  execution: DimensionNarrativeSchema.optional(),
})

export const CharacteristicScoreSchema = z.object({
  key: z.string(),
  label: z.string(),
  dimension: VoiceDimensionKeyEnum,
  score: z.number().min(1).max(5),
  isBoolean: z.boolean(),
  method: z.enum(['deterministic', 'llm', 'hybrid']),
  evidence: z.string().optional(),
  findingTitle: z.string().optional(),
  improvement: z.string().optional(),
  sectionName: z.string().optional(),
  flaggedExcerpt: z.string().optional(),
  rawValue: z.record(z.unknown()).optional(),
})

export const AnalysisModeSchema = z.enum(['minimal', 'full'])

export const PageAnalysisResultSchema = z.object({
  url: z.string().optional(),
  pageType: z.string().optional(),
  status: z.enum(['pending', 'processing', 'completed', 'failed']),
  contentType: z.string(),
  voiceScores: VoiceScoresSchema,
  caiScore: DimensionScoreSchema,
  deterministicMetrics: z.record(z.unknown()).optional(),
  deterministicFindings: z.array(DeterministicFindingSchema).optional(),
  deterministicStrengths: z.array(DeterministicStrengthSchema).optional(),
  llmFindings: z.array(LlmFindingSchema).optional(),
  recommendations: z.array(RecommendationSchema),
  wordCount: z.number(),
  overallAssessment: z.string().optional(),
  topImprovement: z.string().optional(),
  summary: VoiceNarrativeSummarySchema.optional(),
  dimensionNarratives: DimensionNarrativesSchema.optional(),
  /** Characteristic scores with evidence. */
  characteristics: z.array(CharacteristicScoreSchema).optional(),
  /** Plain text for source-panel highlighting (URL/file scans). */
  plainText: z.string().optional(),
})

// ---------------------------------------------------------------------------
// Content debug schemas
// ---------------------------------------------------------------------------

export const LensContentDebugPageSchema = z.object({
  url: z.string().optional(),
  title: z.string().optional(),
  wordCount: z.number(),
  headingCount: z.number(),
  bulletCount: z.number(),
  linkCount: z.number(),
  ctaCount: z.number(),
  sectionCtaCount: z.number().optional(),
  paragraphCount: z.number(),
  sectionCount: z.number(),
  /**
   * Block-model fields. Optional so snapshots captured before the block outline
   * was surfaced still parse — absence means "not captured", not "zero".
   */
  listCount: z.number().optional(),
  listItemCount: z.number().optional(),
  structureConfidence: z.string().optional(),
  blockCount: z.number().optional(),
  blockKindCounts: z.record(z.string(), z.number()).optional(),
  readingSampleFormat: z.enum(['structure-tagged', 'plain']).optional(),
  readingSampleTruncated: z.boolean().optional(),
  headingsTruncated: z.boolean().optional(),
  sectionsTruncated: z.boolean().optional(),
  bulletsTruncated: z.boolean().optional(),
  emphasisReported: z.boolean().optional(),
  emphasisTruncated: z.boolean().optional(),
  blocks: z
    .array(
      z.object({
        kind: z.string(),
        level: z.number().optional(),
        ordered: z.boolean().optional(),
        preview: z.string(),
      }),
    )
    .optional(),
  /** Full, uncapped block content grouped by physical page, in order. */
  blockPages: z
    .array(
      z.object({
        page: z.number(),
        blocks: z.array(
          z.object({
            kind: z.string(),
            level: z.number().optional(),
            ordered: z.boolean().optional(),
            depth: z.number().optional(),
            text: z.string().optional(),
            cells: z.array(z.string()).optional(),
            format: z
              .object({
                fontFamily: z.string().optional(),
                fontSizePt: z.number().optional(),
                bold: z.boolean().optional(),
                italic: z.boolean().optional(),
                color: z.string().optional(),
              })
              .optional(),
          }),
        ),
      }),
    )
    .optional(),
  /** True when blocks carry native page stamps (PDF/PPTX/DOCX/ODT breaks). */
  physicalPagesLocated: z.boolean().optional(),
  /** Set when extracted text exists that no block accounts for. */
  unassignedContent: z
    .object({
      plainTextWordCount: z.number(),
      blockWordCount: z.number(),
      gapWordCount: z.number(),
      note: z.string(),
    })
    .nullable()
    .optional(),
  headings: z.array(z.object({ level: z.number(), text: z.string() })),
  sections: z
    .array(
      z.object({
        index: z.number(),
        role: z.string(),
        primaryTitle: z.string().optional(),
        subtitle: z.string().optional(),
        titleWordCount: z.number(),
        subtitleWordCount: z.number(),
        bodyWordCount: z.number(),
        bodyPreview: z.string(),
        ctaCount: z.number(),
        layoutHint: z.string().optional(),
      }),
    )
    .optional(),
  plainTextPreview: z.string(),
  extractionNote: z.string().optional(),
  intakeWarnings: z
    .array(
      z.object({
        code: z.string(),
        severity: z.string(),
        message: z.string(),
        dimension: z.string().optional(),
      }),
    )
    .optional(),
  structureConfidenceByDimension: z.record(z.string()).optional(),
  evidenceCount: z.number().optional(),
  candidateCount: z.number().optional(),
})

export const LensContentDebugSchema = z.object({
  pages: z.array(LensContentDebugPageSchema),
  note: z.string(),
})

export type LensContentDebugSnapshot = z.infer<typeof LensContentDebugSchema>

/** Admin-only pipeline stage trace (full or reconstructed). */
export const LensPipelineDebugSchema = z.unknown()

export type LensPipelineDebugSnapshot = {
  engineVersion?: string
  scoringPipeline?: string
  completeness?: 'full' | 'reconstructed'
  capturedAt?: string
  contentType?: string
  pages?: unknown[]
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Engine result envelope
// ---------------------------------------------------------------------------

export const LensEngineResultSchema = z.object({
  assessmentType: z.enum(['single', 'website_scan']),
  contentType: z.string(),
  status: z.enum(['pending', 'processing', 'completed', 'failed']),
  sourceUrl: z.string().optional(),
  domain: z.string().optional(),
  voiceScores: VoiceScoresSchema,
  caiScore: DimensionScoreSchema,
  pages: z.array(PageAnalysisResultSchema),
  tokenUsage: z.object({
    model: z.string(),
    promptTokens: z.number(),
    completionTokens: z.number(),
    totalTokens: z.number(),
    estimatedCostUsd: z.number(),
    llmCallCount: z.number().optional(),
  }),
  overallAssessment: z.string().optional(),
  topImprovement: z.string().optional(),
  aggregateRecommendations: z.array(RecommendationSchema).optional(),
  analysisMode: AnalysisModeSchema.optional(),
  summary: VoiceNarrativeSummarySchema.optional(),
  pipeline_debug: z.unknown().optional(),
  /**
   * Reproducibility stamps from the engine (weights_source / weights_version).
   * Optional so older cached envelopes still parse; present on fresh analyzes.
   */
  analysisVersions: z
    .object({
      engine_version: z.string().optional(),
      model: z.string().optional(),
      weights_version: z.string().nullable().optional(),
      weights_source: z.enum(['database', 'fallback']).nullable().optional(),
      prompt_version: z.string().optional(),
      prompt_hash: z.string().optional(),
      rubric_version: z.string().optional(),
      rating_logic_version: z.string().optional(),
    })
    .passthrough()
    .optional(),
  /** Nested report on legacy envelopes — promoted to top-level by parseEnrichedResult. */
  report: LensReportPayloadSchema.optional(),
})

export const LensV6EnrichedResultSchema = z.object({
  engine_version: z.string(),
  profile_id: z.string(),
  assessment_id: z.string().uuid().optional(),
  cache_hit: z.boolean(),
  result: LensEngineResultSchema,
  content_debug: LensContentDebugSchema.optional(),
  pipeline_debug: z.unknown().optional(),
  /** Trust-first / canonical report — present for engine 8.0.0+. Primary UI contract. */
  report: LensReportPayloadSchema.optional(),
  /** Session-only placeholder→original map. Never persisted. */
  pii_redaction_map: z.record(z.string()).optional(),
  /** Revision chain: the assessment this one was created to improve. */
  parent_assessment_id: z.string().uuid().optional(),
  revision_comparison_status: z.string().optional(),
  revision_comparison: z.unknown().optional(),
})

export const LensV6ScanJobSchema = z.object({
  job_id: z.string().uuid(),
  status: z.enum(['queued', 'running', 'completed', 'partial', 'failed']),
  job_type: z.enum(['single', 'website_scan']).optional(),
  progress_current: z.number(),
  progress_total: z.number(),
  assessment_id: z.string().uuid().nullable().optional(),
  error_json: z.unknown().nullable().optional(),
  engine_version: z.string().optional(),
})

export type LensEngineResult = z.infer<typeof LensEngineResultSchema>
export type LensV6EnrichedResult = z.infer<typeof LensV6EnrichedResultSchema>
export type LensV6ScanJob = z.infer<typeof LensV6ScanJobSchema>
export type LensV6PageResult = z.infer<typeof PageAnalysisResultSchema>
export type CharacteristicScore = z.infer<typeof CharacteristicScoreSchema>

// ---------------------------------------------------------------------------
// Analyze input and session state (v6 engine)
// ---------------------------------------------------------------------------

export interface LensV6AnalyzeInput {
  content?: string
  sourceUrl?: string
  /** Multi-page site scan (1–3 same-site URLs). */
  sourceUrls?: string[]
  file?: LensFilePayload
  contentType?: LensContentType
  contentLabel?: string
  audienceType?: LensAudienceType
  audienceLabel?: string
  context?: string
  skipCache?: boolean
  // Telemetry fields — not used by the analysis engine, only logged
  sessionId?: string
  classifiedContentType?: string
  classifiedAudienceType?: string
  classifiedPurpose?: string
  classifiedDetectedIntent?: string
  classifiedActionRequired?: boolean
  contentTypeConfidence?: number
  audienceTypeConfidence?: number
  purposeConfidence?: number
  classificationOverridden?: boolean
  /** Revision chain: links this analysis to the original it revises. */
  parentAssessmentId?: string
  fileMimeType?: string
  fileExtension?: string
  fileSizeBytes?: number
  sourcePageCount?: number
  userInputContext?: string
}

export interface LensV6SessionState {
  engineVersion: string
  profileId: string
  assessmentId?: string
  /** Present after analysis completes. Absent while a stream is pending. */
  result?: LensV6EnrichedResult
  contentType: string
  contentLabel?: string
  sourceType: string
  fileName?: string
  url?: string
  audienceType?: string
  audienceLabel?: string
  context?: string
  /** Original analyze payload for re-run / skip-cache. */
  analyzeInput?: LensV6AnalyzeInput
  /** True when results page should run the v7 analyze → summarize pipeline. */
  pendingV7Pipeline?: boolean
  timestamp: number
}

export const LENS_V6_SESSION_KEY = 'lens_v6_session_state'

// ===========================================================================
// Free audit schemas (extends engine result for public free audits)
// ===========================================================================

export const SignatureRewriteMomentSchema = z.object({
  originalSentence: z.string(),
  sourceUrl: z.string(),
  sourcePageTitle: z.string(),
  autismRewrite: z.string(),
  adhdRewrite: z.string(),
  dyslexiaRewrite: z.string(),
  explanation: z.string(),
})

export const FreeAuditScanCoverageSchema = z.object({
  pagesScanned: z.number(),
  pagesFailed: z.number(),
  failedUrls: z.array(
    z.object({
      url: z.string(),
      error: z.string(),
    }),
  ),
  aggregationWeighting: z.literal('equal'),
})

export const FreeAuditV6ResultSchema = LensEngineResultSchema.extend({
  organizationName: z.string(),
  pageSummaries: z
    .array(
      z.object({
        url: z.string(),
        title: z.string(),
      }),
    )
    .optional(),
  signatureRewrite: SignatureRewriteMomentSchema.optional(),
  scanCoverage: FreeAuditScanCoverageSchema,
})

export const FreeAuditV6ReportSchema = z.object({
  engine_version: z.string(),
  profile_id: z.string(),
  cache_hit: z.literal(false),
  result: FreeAuditV6ResultSchema,
  /** Canonical LensReport (free_audit serialization). */
  report: LensReportPayloadSchema.optional(),
  /** Alias kept for older readers / enrichment skip. */
  lensReport: LensReportPayloadSchema.optional(),
})

export type SignatureRewriteMoment = z.infer<typeof SignatureRewriteMomentSchema>
export type FreeAuditScanCoverage = z.infer<typeof FreeAuditScanCoverageSchema>
export type FreeAuditV6Report = z.infer<typeof FreeAuditV6ReportSchema>

// Dead code removed (Jul 28): FreeAuditTopImprovement, FreeAuditPageSummary,
// FreeAuditReportViewModel — zero importers (legacy view model types).

// Dead code removed (Jul 28): LensObservationSchema, LensConditionSectionSchema,
// LensFrictionPatternSchema, LensSignatureComparisonSchema, LensReportJsonSchema
// and their inferred types — legacy B2B report shapes with zero importers.
// The canonical LensReport contract replaced these.

export const LensReportResponseSchema = z.object({
  id: z.string().uuid(),
  domain: z.string(),
  created_at: z.string(),
  expires_at: z.string().nullable().optional(),
  /** Engine version from DB (`engine_version`), e.g. "6.0.0". Legacy numeric values still accepted. */
  version: z.union([z.string(), z.number()]).nullable().optional(),
  /** May be null when language was not detected / stored. */
  language: z.string().nullable().optional(),
  /**
   * Transport envelope only — shape varies (legacy marketing JSON, free-audit v6,
   * or `lens_engine_result_v6`). Canonical LensReport extraction happens in the page.
   */
  report: z.unknown(),
  _meta: z
    .object({
      enrichment_status: z.string().nullable().optional(),
    })
    .optional(),
})

export type LensReportResponse = z.infer<typeof LensReportResponseSchema>

// Dead code removed (Jul 28): CONDITION_LABELS — depended on removed LensConditionSection.

// ===========================================================================
// Self-serve content analyzer (/lens upload flow)
// ===========================================================================

export type LensContentType =
  | 'email'
  | 'chat_message'
  | 'announcement'
  | 'meeting_notes'
  | 'policy'
  | 'procedure'
  | 'form'
  | 'report'
  | 'job_posting'
  | 'website'
  | 'full-website'
  | 'landing_page'
  | 'other'
  // Legacy UI values still stored on older sessions
  | 'memo'
  | 'letter'
  | 'onboarding'
  | 'review'
  | 'feedback'
  | 'job_description'
  | 'social'
  | 'call_for_applications'
  | 'documentation'

export type LensAudienceType =
  | 'all_staff'
  | 'executive'
  | 'management'
  | 'coworker'
  | 'hr'
  | 'new_hire'
  | 'applicant'
  | 'client'
  | 'vendor'
  | 'partner'
  | 'public'
  | 'other'

export type LensSourceType = 'paste' | 'file' | 'url'

export interface LensFilePayload {
  name: string
  mime_type: string
  /** Raw base64 bytes (no data: URL prefix). */
  data: string
}

// Dead code removed (Jul 28): LensAnalysisRequest, LensAnalysisResponse (deprecated),
// LensSessionState — zero importers. Superseded by LensV6AnalyzeInput / LensV6SessionState.

export const LENS_SESSION_KEY = 'lens_session_state'
export const LENS_SESSION_TTL_MS = 120 * 60 * 1000 // 2 hours

export const CONTENT_TYPE_LABELS: Record<LensContentType, string> = {
  email: 'Email',
  chat_message: 'Chat / Message',
  announcement: 'Announcement / Notice',
  meeting_notes: 'Meeting Notes / Action Items',
  policy: 'Policy',
  procedure: 'Procedure / Instructions',
  form: 'Form / Questionnaire',
  report: 'Report / Assessment',
  job_posting: 'Job Posting',
  website: 'Single Webpage',
  'full-website': 'Multiple Webpages',
  landing_page: 'Landing page',
  other: 'Other',
  memo: 'Email',
  letter: 'Letter',
  onboarding: 'Procedure / Instructions',
  review: 'Report / Assessment',
  feedback: 'Feedback / Evaluation',
  job_description: 'Job Posting',
  social: 'Social Caption',
  call_for_applications: 'Announcement / Notice',
  documentation: 'Procedure / Instructions',
}

export const AUDIENCE_TYPE_LABELS: Record<LensAudienceType, string> = {
  all_staff: 'All staff / Company-wide',
  executive: 'Executive / Leadership',
  management: 'Manager / Supervisor',
  coworker: 'Coworker / Team',
  hr: 'HR',
  new_hire: 'New hire',
  applicant: 'Applicant / Candidate',
  client: 'Client / Customer',
  vendor: 'Vendor / Supplier',
  partner: 'Business partner',
  public: 'Public / General audience',
  other: 'Other',
}
